import { useCallback, useEffect, useRef, useState } from 'react';
import { Stage, Container, Graphics, Text } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useMonitorStore, MonitoredSession, ClientInfo } from '../lib/monitorStore';
import { ParticleSystem, Particle } from './ParticleSystem';
import { decodePacket } from '../lib/PacketDecoder';

// Layout constants
const PADDING = 24;
const CLIENT_X = 80; // Fixed left position for clients
const CLIENT_RADIUS = 24;
const HOST_CLUSTER_WIDTH = 180;
const HOST_CLUSTER_HEIGHT = 140;

// Colors
const COLORS = {
  bgPrimary: 0x0d1117,
  bgSecondary: 0x161b22,
  bgTertiary: 0x21262d,
  border: 0x30363d,
  hostGlow: 0x58a6ff,
  clientGlow: 0x3fb950,
  clientPending: 0xf0883e,
  textPrimary: 0xe6edf3,
  textSecondary: 0x8b949e,
  textMuted: 0x6e7681,
  packetBroadcast: 0xf0883e, // orange
  packetUnicast: 0x58a6ff,   // blue
  packetUpstream: 0x3fb950,  // green
};

// Animation constants
const PARTICLE_LIFETIME_DISCOVERY = 600; // ms - fast for frequent discovery
const PARTICLE_LIFETIME_IMPORTANT = 1200; // ms - slower for important packets

interface ClientNodeProps {
  client: ClientInfo;
  x: number;
  y: number;
  index: number;
}

function ClientNode({ client, x, y }: ClientNodeProps) {
  const drawCallback = useCallback(
    (g: PIXI.Graphics) => {
      g.clear();

      const color = client.authenticated ? COLORS.clientGlow : COLORS.clientPending;

      // Glow effect
      g.beginFill(color, 0.15);
      g.drawCircle(0, 0, CLIENT_RADIUS + 12);
      g.endFill();

      // Outer ring
      g.lineStyle(2, color, 0.4);
      g.drawCircle(0, 0, CLIENT_RADIUS + 4);

      // Main circle
      g.beginFill(color, 0.2);
      g.lineStyle(2, color);
      g.drawCircle(0, 0, CLIENT_RADIUS);
      g.endFill();

      // Inner dot
      g.beginFill(color);
      g.drawCircle(0, 0, 6);
      g.endFill();
    },
    [client.authenticated]
  );

  const ipLabel = client.virtualIP || 'connecting...';
  const statusLabel = client.authenticated ? 'auth ✓' : 'pending...';

  return (
    <Container x={x} y={y}>
      <Graphics draw={drawCallback} />
      <Text
        text={ipLabel}
        x={CLIENT_RADIUS + 12}
        y={-8}
        style={
          new PIXI.TextStyle({
            fontFamily: 'SF Mono, Monaco, monospace',
            fontSize: 12,
            fill: COLORS.textPrimary,
          })
        }
      />
      <Text
        text={statusLabel}
        x={CLIENT_RADIUS + 12}
        y={8}
        style={
          new PIXI.TextStyle({
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: 11,
            fill: client.authenticated ? COLORS.clientGlow : COLORS.clientPending,
          })
        }
      />
    </Container>
  );
}

interface HostClusterProps {
  session: MonitoredSession;
  x: number;
  y: number;
  width: number;
  height: number;
}

function HostCluster({ session, x, y, width, height }: HostClusterProps) {
  const drawCallback = useCallback(
    (g: PIXI.Graphics) => {
      g.clear();

      const isConnected = session.hostConnected;
      const glowColor = isConnected ? COLORS.hostGlow : COLORS.border;

      // Outer glow
      if (isConnected) {
        g.beginFill(glowColor, 0.1);
        g.drawRoundedRect(-8, -8, width + 16, height + 16, 20);
        g.endFill();
      }

      // Main container background (squircle)
      g.beginFill(0x1a2332);
      g.lineStyle(2, glowColor, isConnected ? 0.8 : 0.4);
      g.drawRoundedRect(0, 0, width, height, 16);
      g.endFill();
    },
    [session.hostConnected, width, height]
  );

  const statsText = `↓${session.packetStats.hostToClient} ↑${session.packetStats.clientToHost}`;

  return (
    <Container x={x} y={y}>
      <Graphics draw={drawCallback} />
      {/* CALNET CLUSTER label */}
      <Text
        text="CALNET CLUSTER"
        x={width / 2}
        y={height / 2 - 12}
        anchor={0.5}
        style={
          new PIXI.TextStyle({
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            fontSize: 11,
            fontWeight: '600',
            fill: session.hostConnected ? COLORS.hostGlow : COLORS.textMuted,
            letterSpacing: 1,
          })
        }
      />
      {/* Packet stats */}
      <Text
        text={statsText}
        x={width / 2}
        y={height / 2 + 10}
        anchor={0.5}
        style={
          new PIXI.TextStyle({
            fontFamily: 'SF Mono, Monaco, monospace',
            fontSize: 12,
            fill: COLORS.textSecondary,
          })
        }
      />
    </Container>
  );
}

export function SessionGraph() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const { sessions, selectedSessionId, selectSession, recentPackets, decryptionKeys } = useMonitorStore();
  const [particles, setParticles] = useState<Particle[]>([]);
  const particleIdRef = useRef(0);
  const lastPacketRef = useRef<string | null>(null);

  // Track dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Get the active session (selected or first available)
  const sessionArray = Array.from(sessions.values());
  const activeSession = selectedSessionId
    ? sessions.get(selectedSessionId)
    : sessionArray[0];

  // Calculate layout positions - clients on far LEFT, host on far RIGHT
  const hostAreaX = dimensions.width - PADDING - HOST_CLUSTER_WIDTH;
  const hostAreaY = (dimensions.height - HOST_CLUSTER_HEIGHT) / 2;

  // Calculate client positions with vertical spread on the LEFT side
  const getClientPosition = (index: number, total: number) => {
    const availableHeight = dimensions.height - PADDING * 4;
    const spacing = total > 1 ? availableHeight / (total + 1) : availableHeight / 2;
    return {
      x: CLIENT_X,
      y: PADDING * 2 + spacing * (index + 1),
    };
  };

  // Host cluster connection point (left edge of cluster, vertically centered)
  const hostConnectionPoint = {
    x: hostAreaX,
    y: hostAreaY + HOST_CLUSTER_HEIGHT / 2,
  };

  // Spawn particles from packet events
  useEffect(() => {
    if (recentPackets.length === 0 || !activeSession) return;

    const latestPacket = recentPackets[0];

    // Skip if we've already processed this packet
    const packetKey = `${latestPacket.sessionId}-${latestPacket.timestamp}-${latestPacket.direction}`;
    if (lastPacketRef.current === packetKey) return;
    lastPacketRef.current = packetKey;

    // Only show packets for the active session
    if (latestPacket.sessionId !== activeSession.identifier) return;

    const isUpstream = latestPacket.direction === 'client_to_host';
    console.log('[SessionGraph] packet direction:', latestPacket.direction, 'isUpstream:', isUpstream);

    const color =
      latestPacket.direction === 'client_to_host'
        ? COLORS.packetUpstream
        : latestPacket.targetType === 'broadcast'
        ? COLORS.packetBroadcast
        : COLORS.packetUnicast;

    // Determine source/target positions
    const clientCount = activeSession.clients.length;
    const clientIndex = Math.floor(Math.random() * Math.max(1, clientCount));
    const clientPos = getClientPosition(clientIndex, Math.max(1, clientCount));

    const startX = isUpstream ? clientPos.x : hostConnectionPoint.x;
    const startY = isUpstream ? clientPos.y : hostConnectionPoint.y;
    const targetX = isUpstream ? hostConnectionPoint.x : clientPos.x;
    const targetY = isUpstream ? hostConnectionPoint.y : clientPos.y;

    // Spawn particle immediately, decode label async
    const particleId = particleIdRef.current++;
    const netKey = decryptionKeys.get(activeSession.identifier);

    // Create particle with placeholder (will update label/arc async based on packet type)
    const newParticle: Particle = {
      id: particleId,
      x: startX,
      y: startY,
      startX,
      startY,
      targetX,
      targetY,
      vx: 0,
      vy: 0,
      color,
      size: 8,
      alpha: 1,
      initialAlpha: 1,
      lifetime: PARTICLE_LIFETIME_IMPORTANT, // Default to important, will adjust for discovery
      born: Date.now(),
      label: '',
      arcHeight: 40, // Default arc for important packets
      isUpstream,
    };

    setParticles((prev) => [...prev.slice(-50), newParticle]);

    // Decode packet async and update particle label
    if (latestPacket.payloadPreview) {
      decodePacket(latestPacket.payloadPreview, netKey).then((decoded) => {
        if (!decoded) return;

        let label = '';
        let isDiscovery = false;

        // Skip error states
        if (
          decoded.payloadType !== 'error' &&
          decoded.payloadType !== 'short' &&
          decoded.payloadType !== 'invalid' &&
          decoded.payloadType !== 'None'
        ) {
          label = decoded.subType
            ? `${decoded.payloadType}:${decoded.subType}`
            : decoded.payloadType;
          isDiscovery = decoded.isDiscovery || false;
        }

        // Update the particle with decoded info
        setParticles((prev) =>
          prev.map((p) =>
            p.id === particleId
              ? {
                  ...p,
                  label: isDiscovery ? '' : label,
                  size: isDiscovery ? 4 : 8,
                  alpha: isDiscovery ? 0.5 : 1,
                  initialAlpha: isDiscovery ? 0.5 : 1,
                  lifetime: isDiscovery ? PARTICLE_LIFETIME_DISCOVERY : PARTICLE_LIFETIME_IMPORTANT,
                  arcHeight: isDiscovery ? 0 : 50, // No arc for discovery, nice arc for important
                }
              : p
          )
        );
      });
    }
  }, [recentPackets, activeSession, decryptionKeys, hostConnectionPoint.x, hostConnectionPoint.y]);

  // Time-based animation loop for particles
  useEffect(() => {
    let animationId: number;

    const animate = () => {
      const now = Date.now();
      setParticles((prev) =>
        prev
          .map((p) => {
            const age = now - p.born;
            const progress = Math.min(age / p.lifetime, 1);

            // Ease-out interpolation (starts fast, slows at end)
            const eased = 1 - Math.pow(1 - progress, 3);

            // Interpolate position from start to target
            const baseX = p.startX + (p.targetX - p.startX) * eased;
            const baseY = p.startY + (p.targetY - p.startY) * eased;

            // Apply arc: parabolic curve that peaks at progress=0.5
            // arc = 4 * arcHeight * progress * (1 - progress) gives max at 0.5
            // Upstream (client→host) arcs upward, downstream (host→client) arcs downward
            const arcOffset = 4 * p.arcHeight * progress * (1 - progress);
            const newX = baseX;
            const newY = p.isUpstream ? baseY - arcOffset : baseY + arcOffset;

            // Fade out in last 20% of lifetime (preserving initial alpha)
            const fadeMultiplier = progress > 0.8 ? 1 - ((progress - 0.8) / 0.2) : 1;
            const alpha = p.initialAlpha * fadeMultiplier;

            return {
              ...p,
              x: newX,
              y: newY,
              alpha,
            };
          })
          .filter((p) => {
            const age = now - p.born;
            return age < p.lifetime;
          })
      );

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  const drawBackground = useCallback(
    (g: PIXI.Graphics) => {
      g.clear();
      g.beginFill(COLORS.bgPrimary);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();

      // Draw connection lines from clients to host when session active
      if (activeSession && activeSession.clients.length > 0) {
        g.lineStyle(1, COLORS.border, 0.3);
        activeSession.clients.forEach((_, i) => {
          const clientPos = getClientPosition(i, activeSession.clients.length);
          g.moveTo(clientPos.x + CLIENT_RADIUS, clientPos.y);
          g.lineTo(hostConnectionPoint.x - 10, hostConnectionPoint.y);
        });
      }
    },
    [dimensions, activeSession, hostConnectionPoint]
  );

  // Draw session selector if multiple sessions
  const drawSessionSelector = useCallback(
    (g: PIXI.Graphics) => {
      g.clear();
      if (sessionArray.length <= 1) return;

      // Draw session indicator dots at top
      const dotSize = 8;
      const dotGap = 16;
      const totalWidth = sessionArray.length * dotSize + (sessionArray.length - 1) * dotGap;
      const startX = (dimensions.width - totalWidth) / 2;

      sessionArray.forEach((session, i) => {
        const isActive = activeSession?.identifier === session.identifier;
        g.beginFill(
          isActive ? COLORS.hostGlow : COLORS.border,
          isActive ? 1 : 0.5
        );
        g.drawCircle(startX + i * (dotSize + dotGap), 20, dotSize / 2);
        g.endFill();
      });
    },
    [sessionArray, activeSession, dimensions.width]
  );

  // Handle session selection via clicks on selector dots
  const handleStageClick = useCallback(
    (e: PIXI.FederatedPointerEvent) => {
      if (sessionArray.length <= 1) return;

      const dotSize = 8;
      const dotGap = 16;
      const totalWidth = sessionArray.length * dotSize + (sessionArray.length - 1) * dotGap;
      const startX = (dimensions.width - totalWidth) / 2;

      sessionArray.forEach((session, i) => {
        const dotX = startX + i * (dotSize + dotGap);
        const dx = e.global.x - dotX;
        const dy = e.global.y - 20;
        if (Math.sqrt(dx * dx + dy * dy) < dotSize) {
          selectSession(session.identifier);
        }
      });
    },
    [sessionArray, dimensions.width, selectSession]
  );

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <Stage
        width={dimensions.width}
        height={dimensions.height}
        options={{ backgroundColor: COLORS.bgPrimary, antialias: true }}
      >
        <Graphics draw={drawBackground} />
        <Graphics draw={drawSessionSelector} />

        {!activeSession ? (
          <Container x={dimensions.width / 2} y={dimensions.height / 2}>
            <Text
              text="No active sessions"
              anchor={0.5}
              style={
                new PIXI.TextStyle({
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: 16,
                  fill: COLORS.textSecondary,
                })
              }
            />
            <Text
              text="Sessions will appear when hosts connect"
              y={28}
              anchor={0.5}
              style={
                new PIXI.TextStyle({
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  fontSize: 13,
                  fill: COLORS.textMuted,
                })
              }
            />
          </Container>
        ) : (
          <Container x={0} y={0} eventMode="static" onclick={handleStageClick}>
            {/* Client nodes on LEFT */}
            {activeSession.clients.length === 0 ? (
              <Container x={CLIENT_X} y={dimensions.height / 2}>
                <Graphics
                  draw={(g) => {
                    g.clear();
                    // Inner circle
                    g.lineStyle(2, COLORS.border, 0.3);
                    g.drawCircle(0, 0, CLIENT_RADIUS);
                    // Outer dashed effect (simulated with dots)
                    g.lineStyle(0);
                    const outerRadius = CLIENT_RADIUS + 8;
                    const dotCount = 16;
                    for (let i = 0; i < dotCount; i++) {
                      const angle = (i / dotCount) * Math.PI * 2;
                      g.beginFill(COLORS.border, 0.2);
                      g.drawCircle(
                        Math.cos(angle) * outerRadius,
                        Math.sin(angle) * outerRadius,
                        2
                      );
                      g.endFill();
                    }
                  }}
                />
                <Text
                  text="No clients"
                  x={CLIENT_RADIUS + 12}
                  y={-4}
                  style={
                    new PIXI.TextStyle({
                      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                      fontSize: 12,
                      fill: COLORS.textMuted,
                    })
                  }
                />
              </Container>
            ) : (
              activeSession.clients.map((client, i) => {
                const pos = getClientPosition(i, activeSession.clients.length);
                return (
                  <ClientNode
                    key={client.clientId}
                    client={client}
                    x={pos.x}
                    y={pos.y}
                    index={i}
                  />
                );
              })
            )}

            {/* Host cluster on RIGHT */}
            <HostCluster
              session={activeSession}
              x={hostAreaX}
              y={hostAreaY}
              width={HOST_CLUSTER_WIDTH}
              height={HOST_CLUSTER_HEIGHT}
            />

            {/* Particles */}
            <ParticleSystem particles={particles} />
          </Container>
        )}
      </Stage>
    </div>
  );
}
